import React, { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { FaPaperPlane, FaSmile, FaImage, FaLink, FaEllipsisH, FaTimes, FaReply, FaComments, FaLock, FaThumbtack, FaArrowLeft } from "react-icons/fa";
import { useToast } from "@/components/ui/use-toast";
import { addDoc, collection, serverTimestamp, query, orderBy, onSnapshot, where, getDoc, getDocs, updateDoc, doc, increment } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { useAuth } from "@/contexts/AuthContext";
import { motion, AnimatePresence } from "framer-motion";
import EmojiPicker, { Theme as EmojiTheme } from "emoji-picker-react";
import { storage } from "@/lib/firebase";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

// chatRooms constant removed, room management is now handled by Community.tsx

interface Reaction {
  emoji: string;
  users: string[];
}

interface Message {
  id: string;
  text: string;
  userId: string;
  userName: string;
  userAvatar?: string;
  createdAt: any;
  imageUrl?: string;
  reactions?: Reaction[];
  replyTo?: {
    id: string;
    userName: string;
    text: string;
  } | null;
  isPinned?: boolean;
  pinnedAt?: any; // Firestore FieldValue.serverTimestamp()
  // Threading related fields
  threadId?: string; // ID of the thread this message belongs to (could be the ID of the first message in the thread)
  parentMessageId?: string; // ID of the message this is a direct reply to within a thread, or the ID of the thread starter
  replyCount?: number; // Number of direct replies to this message, if it's a thread starter
  roomId: string; // Added roomId to Message interface
  isThreadStarter?: boolean; // True if this message starts a new thread
}

interface ChatForumProps {
  activeRoomId: string;
}

export const ChatForum = ({ activeRoomId }: ChatForumProps) => {
  const { currentUser, userProfile } = useAuth();
  const [mainMessages, setMainMessages] = useState<Message[]>([]);
  const [currentThreadMessages, setCurrentThreadMessages] = useState<Message[]>([]);
  const [activeThreadId, setActiveThreadId] = useState<string | null>(null);
  const [newMessage, setNewMessage] = useState("");
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [replyTo, setReplyTo] = useState<Message | null>(null);
  const [onlineUsers, setOnlineUsers] = useState<any[]>([]); // Placeholder for online users
  const [isLoading, setIsLoading] = useState(true);
  const [hoveredMessageId, setHoveredMessageId] = useState<string | null>(null);
  const displayedMessages = activeThreadId ? currentThreadMessages : mainMessages;
  const { toast } = useToast();

  useEffect(() => {
    if (!currentUser || !activeRoomId) {
      setMainMessages([]);
      setCurrentThreadMessages([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    let q;
    const messagesCollectionRef = collection(db, "communityMessages");

    if (activeThreadId) {
      // Fetch messages for the active thread
      q = query(
        messagesCollectionRef,
        where("roomId", "==", activeRoomId),
        where("threadId", "==", activeThreadId)
      );
    } else {
      // Fetch main messages (not part of a thread or are thread starters)
      // TEMPORARY: Removing threadId null filter for debugging 400 error
      q = query(
        messagesCollectionRef,
        where("roomId", "==", activeRoomId)
      );
    }

    const unsubscribe = onSnapshot(q, (snapshot) => {
      console.log("Fetching messages for room:", activeRoomId, "thread:", activeThreadId);
      console.log("Snapshot received. Number of documents:", snapshot.docs.length);
      if (snapshot.empty) {
        console.log("Snapshot is empty.");
      } else {
        console.log("First document data:", snapshot.docs[0].data());
      }
      const fetchedMessages = snapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() } as Message))
        .sort((a, b) => {
          // Sort messages by createdAt timestamp
          const timeA = a.createdAt?.toDate?.() || new Date(0);
          const timeB = b.createdAt?.toDate?.() || new Date(0);
          return timeA.getTime() - timeB.getTime();
        });

      if (activeThreadId) {
        setCurrentThreadMessages(fetchedMessages);
      } else {
        setMainMessages(fetchedMessages);
      }
      setIsLoading(false);
    }, (error) => {
      console.error(`Error fetching messages for room ${activeRoomId} (thread: ${activeThreadId || 'main'}):`, error);
      toast({ variant: "destructive", title: "Error", description: "Failed to load messages. Please try again later." });
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, [activeRoomId, currentUser, activeThreadId]);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [displayedMessages]);

  const handleTogglePinMessage = async (messageId: string, currentIsPinned?: boolean) => {
    if (!currentUser || !activeRoomId) {
      toast({ variant: "destructive", title: "Error", description: "You must be logged in to pin messages." });
      return;
    }
    const messageRef = doc(db, "communityMessages", messageId);
    try {
      await updateDoc(messageRef, {
        isPinned: !currentIsPinned,
        pinnedAt: !currentIsPinned ? serverTimestamp() : null, // Or FieldValue.delete() to remove
      });
      toast({ variant: "default", title: "Success", description: !currentIsPinned ? "Message pinned!" : "Message unpinned." });
    } catch (error) {
      console.error("Error toggling pin status:", error);
      toast({ variant: "destructive", title: "Error", description: "Failed to update pin status." });
    }
  };

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [displayedMessages]);

  const handleSendMessage = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if ((!newMessage.trim() && !selectedImage) || !currentUser || !activeRoomId) return;

    try {
      let imageUrl = null;
      if (selectedImage) {
        const storageRef = ref(storage, `chat_images/${currentUser.uid}/${Date.now()}_${selectedImage.name}`);
        const uploadTask = await uploadBytes(storageRef, selectedImage);
        imageUrl = await getDownloadURL(uploadTask.ref);
      }

      const messageData: Omit<Message, 'id'> & { createdAt: any } = {
        text: newMessage.trim(),
        userId: currentUser.uid,
        userName: userProfile ? `${userProfile.firstName} ${userProfile.lastName}` : (currentUser.displayName || "Anonymous"),
        userAvatar: currentUser.photoURL || undefined, // Use currentUser.photoURL
        roomId: activeRoomId, // roomId is now part of the Message interface
        createdAt: serverTimestamp(),
        imageUrl: imageUrl,
        reactions: [],
        replyTo: replyTo ? { id: replyTo.id, userName: replyTo.userName, text: replyTo.text } : null,
        // Threading fields
        threadId: activeThreadId || undefined,
        parentMessageId: activeThreadId || undefined,
        isThreadStarter: activeThreadId ? false : true, // Set isThreadStarter based on activeThreadId
        replyCount: 0,
      };

      Object.keys(messageData).forEach(key => messageData[key as keyof typeof messageData] === undefined && delete messageData[key as keyof typeof messageData]);

      const newDocRef = await addDoc(collection(db, "communityMessages"), messageData);

      // If the message is part of an active thread, increment the reply count of the thread starter message
      if (activeThreadId) {
        const threadStarterRef = doc(db, "communityMessages", activeThreadId);
        try {
          await updateDoc(threadStarterRef, {
            replyCount: increment(1)
          });
        } catch (error) {
          console.error("Error incrementing reply count:", error);
          // Non-critical error, so we don't need to toast heavily, but good to log.
        }
      }

      setNewMessage("");
      setSelectedImage(null);
      setShowEmojiPicker(false);
      setReplyTo(null);
    } catch (error) {
      console.error("Error sending message:", error);
      toast({ variant: "destructive", title: "Error", description: "Failed to send message" });
    }
  };

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedImage(e.target.files[0]);
    }
  };

  const handleRemoveImage = () => {
    setSelectedImage(null);
  };

  const handleAddEmoji = (emojiData: { emoji: string }) => {
    setNewMessage(prevMsg => prevMsg + emojiData.emoji);
  };

  const handleToggleEmojiPicker = () => {
    setShowEmojiPicker(prev => !prev);
  };

  const handleReact = async (messageId: string, emoji: string) => {
    if (!currentUser) return;

    const messageRef = doc(db, "communityMessages", messageId);
    try {
      const messageDoc = await getDoc(messageRef);
      if (messageDoc.exists()) {
        const messageData = messageDoc.data() as Message;
        let existingReactions = messageData.reactions ? [...messageData.reactions] : [];
        const currentUserId = currentUser.uid;

        // Check if user has already reacted with any emoji
        let userPreviousReactionIndex = -1;
        for(let i=0; i < existingReactions.length; i++){
            if(existingReactions[i].users.includes(currentUserId)){
                userPreviousReactionIndex = i;
                break;
            }
        }

        if (userPreviousReactionIndex !== -1) {
          // User has reacted before
          const previousReaction = existingReactions[userPreviousReactionIndex];
          if (previousReaction.emoji === emoji) {
            // User clicked the same emoji again - remove reaction
            previousReaction.users = previousReaction.users.filter(uid => uid !== currentUserId);
            if (previousReaction.users.length === 0) {
              existingReactions.splice(userPreviousReactionIndex, 1);
            }
          } else {
            // User clicked a different emoji - change reaction
            previousReaction.users = previousReaction.users.filter(uid => uid !== currentUserId);
            if (previousReaction.users.length === 0) {
              existingReactions.splice(userPreviousReactionIndex, 1);
            }
            // Add to new emoji
            const newEmojiReaction = existingReactions.find(r => r.emoji === emoji);
            if (newEmojiReaction) {
              newEmojiReaction.users.push(currentUserId);
            } else {
              existingReactions.push({ emoji: emoji, users: [currentUserId] });
            }
          }
        } else {
          // User is reacting for the first time (or first time after removing all previous reactions)
          const newEmojiReaction = existingReactions.find(r => r.emoji === emoji);
          if (newEmojiReaction) {
            newEmojiReaction.users.push(currentUserId);
          } else {
            existingReactions.push({ emoji: emoji, users: [currentUserId] });
          }
        }
        await updateDoc(messageRef, { reactions: existingReactions });
      } else {
         // Handle case where message no longer exists (e.g., deleted by another user)
         console.warn(`Attempted to react to non-existent message: ${messageId}`);
         toast({ variant: "destructive", title: "Error", description: "Message not found or already deleted." });
      }
    } catch (error) {
      console.error("Error reacting to message:", error);
      toast({ variant: "destructive", title: "Error", description: "Failed to react to message." });
    }
  };

  const handleReplyClick = (message: Message) => {
    setReplyTo(message);
  };

/*
  const handleStartOrOpenThread = async (messageToThread: Message) => {
    if (!currentUser) {
      toast({ variant: "destructive", title: "Error", description: "Please log in to interact with threads." });
      return;
    }
    // If the message is not yet a thread starter, mark it as such.
    if (!messageToThread.isThreadStarter) {
      const messageRef = doc(db, "communityMessages", messageToThread.id);
      try {
        await updateDoc(messageRef, {
          isThreadStarter: true,
          threadId: messageToThread.id, // It becomes the root of its own thread
          parentMessageId: null,       // A thread starter has no parent message in this context
          // replyCount will be initialized to 0 or incremented by new replies
        });
        toast({ variant: "default", title: "Success", description: `Thread started: "${messageToThread.text.substring(0, 20)}..."` });
      } catch (error) {
        console.error("Error marking message as thread starter:", error);
        toast({ variant: "destructive", title: "Error", description: "Could not start thread." });
        return; // Don't switch view if Firestore update fails
      }
    }
    // Set the active thread ID to view this thread's messages.
    setActiveThreadId(messageToThread.id);
  };
*/

  const handleCancelReply = () => {
    setReplyTo(null);
  };

  const formatTime = (timestamp: any): string => {
    if (!timestamp) return "sending...";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    if (isNaN(date.getTime())) {
      return "just now"; // Or handle as an error/invalid date string
    }

    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 5) return "just now";
    if (diffInSeconds < 60) return `${diffInSeconds}s ago`;
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h ago`;
    
    // For dates older than 24 hours, show month and day
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };


  return (
    <Card className="flex flex-col bg-black/20 backdrop-blur-sm border-gray-800 w-full h-full">
      <CardHeader className="py-3 border-b border-gray-800">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            {activeThreadId && (
              <Button variant="ghost" onClick={() => setActiveThreadId(null)} className="text-sm text-truth hover:text-truth/80 p-1.5 h-auto">
                <FaArrowLeft className="mr-1.5 h-3.5 w-3.5" /> Back
              </Button>
            )}
            <div>
              <CardTitle className="text-xl font-bold text-truth flex items-center gap-2">
                <FaComments /> Chat: {activeRoomId}
              </CardTitle>
          </div>
        </div>
        <div className="flex gap-2">
          {/* <Button variant="outline" size="icon" className="h-8 w-8">
            <FaSearch className="h-3.5 w-3.5" />
          </Button> */}
          <Button variant="outline" size="icon" className="h-8 w-8">
            <FaEllipsisH className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
    </CardHeader>

    <CardContent className="flex-grow overflow-y-auto p-4 space-y-4">
      <AnimatePresence>
        {displayedMessages.map((message) => {
          const isCurrentUser = message.userId === currentUser?.uid;
          const timeDisplay = formatTime(message.createdAt);
          return (
            <motion.div
              key={message.id}
              layout
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className={`flex mb-3 relative group ${isCurrentUser ? "justify-end" : "justify-start"}`}
              onMouseEnter={() => setHoveredMessageId(message.id)}
              onMouseLeave={() => setHoveredMessageId(null)}
            >
              {!isCurrentUser && (
                <Avatar className="h-8 w-8 mr-2 flex-shrink-0">
                  <AvatarImage src={message.userAvatar || undefined} alt={message.userName} />
                  <AvatarFallback>{message.userName?.substring(0, 1).toUpperCase() || "U"}</AvatarFallback>
                </Avatar>
              )}
              <div className={`max-w-[70%] p-2.5 rounded-lg shadow ${isCurrentUser ? "bg-truth/20 text-gray-200 rounded-br-none" : "bg-gray-700/60 text-gray-300 rounded-bl-none"}`}>
                <div className={`flex items-center gap-2 text-xs mb-1.5 ${isCurrentUser ? "justify-end" : ""}`}>
                  {!isCurrentUser && <span className="font-semibold text-truth">{message.userName}</span>}
                  <span className="text-gray-400">{timeDisplay}</span>
                  {message.isPinned && <FaThumbtack className="h-3 w-3 text-truth/70" title="Pinned" />}
                  {message.isThreadStarter && message.replyCount != null && (
                    <span className="text-xs text-blue-400 ml-1">{message.replyCount > 0 ? `(${message.replyCount} replies)` : '(Start a thread)'}</span>
                  )}
                </div>

                {message.replyTo && (
                  <div className="bg-black/25 border-l-2 border-truth/50 rounded-sm p-1.5 mb-1.5 text-xs italic text-gray-300 overflow-hidden">
                    <div className="font-semibold text-truth/90 mb-0.5">Replying to {message.replyTo.userName}</div>
                    <div className="truncate text-gray-400">{message.replyTo.text}</div>
                  </div>
                )}
                {message.imageUrl && (
                  <img src={message.imageUrl} alt="attachment" className="rounded-md mb-1.5 max-h-60 object-cover cursor-pointer" onClick={() => window.open(message.imageUrl, '_blank')} />
                )}
                <p className="text-sm whitespace-pre-wrap break-words">{message.text}</p>
                
                {/* Reactions display */}
                {message.reactions && message.reactions.length > 0 && (
                  <div className={`flex gap-1 mt-1.5 ${isCurrentUser ? 'justify-end' : 'justify-start'}`}>
                    {message.reactions.map(reaction => (
                      <button
                        key={reaction.emoji}
                        className={`px-1.5 py-0.5 rounded-full border text-xs transition-colors
                          ${reaction.users.includes(currentUser?.uid || '') ? 'bg-blue-500/40 border-blue-500/60 text-blue-200' : 'bg-gray-600/50 border-gray-500/70 text-gray-300 hover:bg-gray-500/70'}`}
                        onClick={() => handleReact(message.id, reaction.emoji)}
                      >
                        {reaction.emoji} {reaction.users.length}
                      </button>
                    ))}
                  </div>
                )}

                {/* Hover controls */}
                <div 
                  className={`absolute top-0.5 p-0.5 bg-gray-800/70 backdrop-blur-sm shadow-md rounded-md flex gap-0.5 transition-opacity duration-150 ease-in-out ${
                    hoveredMessageId === message.id ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
                  } ${isCurrentUser ? 'left-[-8px] transform -translate-x-full' : 'right-[-8px] transform translate-x-full'}`}
                >
                  <button
                    className="text-gray-300 hover:text-yellow-400 p-1 rounded-md"
                    onClick={() => handleReact(message.id, '👍')} title="Like" // Example, could open full emoji picker
                  >
                    <FaSmile className="h-3.5 w-3.5" />
                  </button>
                  <button
                    className="text-gray-300 hover:text-blue-400 p-1 rounded-md"
                      onClick={() => handleReplyClick(message)} title="Reply"
                  >
                      <FaReply className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            </motion.div>
          );
        })}
        <div ref={messagesEndRef} />
      </AnimatePresence>

      {isLoading && displayedMessages.length === 0 && (
        <div className="flex flex-col items-center justify-center h-full text-gray-400">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-truth"></div>
          <p className="mt-2 text-sm">Loading messages...</p>
        </div>
      )}
      {!isLoading && displayedMessages.length === 0 && currentUser && (
        <div className="flex flex-col items-center justify-center h-full text-gray-400">
          <FaComments className="w-16 h-16 mb-4 text-gray-500" />
          <h3 className="text-lg font-medium">It's quiet in here...</h3>
          <p className="text-sm">Be the first to send a message in {activeRoomId || 'this room'}!</p>
        </div>
      )}
      {!currentUser && (
        <div className="flex flex-col items-center justify-center h-full text-gray-400">
          <FaLock className="w-12 h-12 mb-4 text-gray-500" />
          <h3 className="text-lg font-medium">Sign in to Chat</h3>
          <p className="text-sm">Please sign in to view and send messages.</p>
        </div>
      )}
    </CardContent>

    <CardFooter className="p-3 border-t border-gray-800 relative">
      {replyTo && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 10 }}
          className="absolute bottom-full left-0 right-0 bg-gray-700/90 backdrop-blur-sm border-t border-x border-gray-600 rounded-t-lg p-2 text-sm text-gray-200 flex items-center justify-between shadow-lg"
        >
          <div>
            Replying to <span className="font-semibold text-truth">{replyTo.userName}</span>:
            <span className="ml-1.5 italic truncate max-w-xs inline-block text-gray-300">{replyTo.text}</span>
          </div>
          <button onClick={handleCancelReply} className="text-gray-400 hover:text-red-400">
            <FaTimes />
          </button>
        </motion.div>
      )}
      {showEmojiPicker && (
        <div className="absolute bottom-full mb-2 right-0 md:left-0 md:right-auto z-10">
            <EmojiPicker onEmojiClick={handleAddEmoji} theme={"dark" as EmojiTheme} />
        </div>
      )}
      <form onSubmit={handleSendMessage} className="flex gap-2 w-full items-end">
        <div className="flex-grow relative">
          {selectedImage && (
            <div className="absolute bottom-full left-0 mb-1 p-1 bg-gray-700/80 backdrop-blur-sm rounded-lg shadow-md">
              <img src={URL.createObjectURL(selectedImage)} alt="Selected preview" className="h-16 w-auto object-contain rounded" />
              <button
                type="button"
                onClick={handleRemoveImage}
                className="absolute top-[-5px] right-[-5px] bg-red-500 text-white rounded-full h-4 w-4 flex items-center justify-center text-xs leading-none shadow-md"
              >
                ×
              </button>
            </div>
          )}
          <Input
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder={`Message #${activeRoomId}...`}
            className="flex-1 bg-black/50 border-truth/30 focus:border-truth pr-[70px] min-h-[40px] h-auto resize-none py-2 leading-tight"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSendMessage();
              }
            }}
          />
          <div className="absolute right-1 top-1/2 transform -translate-y-1/2 flex gap-1">
            <label className="cursor-pointer text-gray-400 hover:text-truth p-1.5 rounded-md hover:bg-gray-700/50">
              <FaImage className="h-4 w-4" />
              <input type="file" accept="image/*" className="hidden" onChange={handleImageSelect} />
            </label>
            <button
              type="button"
              onClick={handleToggleEmojiPicker}
              className="text-gray-400 hover:text-yellow-400 p-1.5 rounded-md hover:bg-gray-700/50"
              title="Insert emoji"
            >
              <FaSmile className="h-4 w-4" />
            </button>
          </div>
        </div>
        <Button
          type="submit"
          className="bg-truth hover:bg-truth/90 p-2 h-[40px] w-[40px] flex-shrink-0"
          disabled={(!newMessage.trim() && !selectedImage) || !currentUser || isLoading}
          >
            <FaPaperPlane className="h-4 w-4" />
          </Button>
        </form>
      </CardFooter>
    </Card>
  );
}
